<template>
    <div class="order-details">
      <!-- 顶部操作栏 -->
      <div class="action-bar">
        <div class="left-actions">
          <el-button icon="el-icon-back" size="medium" plain @click="goBack">返回列表</el-button>
        </div>
        <div class="date-info">
          <div v-if="creatorInfo">创建时间：{{ creatorInfo.username || '用户' }} {{ formatDateTime(orderData.created_at) }}</div>
          <div v-if="updaterInfo.username">上次更新：{{ updaterInfo.username }} {{ formatDateTime(orderData.updated_at) }}</div>
        </div>
        <div class="right-actions">
          <el-tag :type="orderData.need_addr ? 'primary' : 'success'">
            {{ orderData.need_addr ? '实体商品' : '虚拟商品' }}
          </el-tag>
        </div>
      </div>
  
      <!-- 主要内容区域 -->
      <div class="main-content">
        <div class="content-grid">
          <!-- 左侧内容列 -->
          <div class="left-column">
            <!-- 商品标题卡片 -->
            <el-card class="product-title-card">
              <div class="product-title">
                <div class="title-content">
                  <i class="el-icon-goods"></i>
                  <span>{{ orderData.title_cn || '-' }}</span>
                </div>
              </div>
            </el-card>
  
            <!-- 订单基本信息卡片 -->
            <el-card class="order-info-card">
              <div class="card-header">
                <span>订单信息</span>
                <div class="header-actions">
                  <template v-if="!editingOrder">
                    <el-button type="text" icon="el-icon-edit" @click="startEditOrder">编辑</el-button>
                  </template>
                  <template v-else>
                    <el-button type="text" icon="el-icon-check" @click="saveOrder">保存</el-button>
                    <el-button type="text" icon="el-icon-close" @click="cancelEditOrder">取消</el-button>
                  </template>
                </div>
              </div>
              <div class="info-grid">
                <template v-if="!editingOrder">
                  <div class="info-section">
                    <div class="info-item">
                      <span class="label">订单编号</span>
                      <span class="value">{{ orderData.order_no || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">订单状态</span>
                      <span class="value">
                        <el-tag :type="getStatusType(orderData.status)">
                          {{ getStatusText(orderData.status) }}
                        </el-tag>
                      </span>
                    </div>
                  </div>
                  <div class="info-section">
                    <div class="info-item">
                      <span class="label">订单金额</span>
                      <span class="value amount">{{ orderData.amount }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">实付金额</span>
                      <span class="value amount">{{ orderData.paid_amount }}</span>
                    </div>
                  </div>
                  <div class="info-section">
                    <div class="info-item">
                      <span class="label">商品数量</span>
                      <span class="value">{{ orderData.quantity || '0' }} 件</span>
                    </div>
                    <div class="info-item">
                      <span class="label">支付时间</span>
                      <span class="value">{{ formatDateTime(orderData.paid_at) || '-' }}</span>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="info-section">
                    <div class="info-item">
                      <span class="label">订单状态</span>
                      <el-select v-model="orderEdit.status" class="full-width">
                        <el-option :value="0" label="待支付"></el-option>
                        <el-option :value="1" label="已支付"></el-option>
                        <el-option :value="2" label="已取消"></el-option>
                        <el-option :value="3" label="已退款"></el-option>
                      </el-select>
                    </div>
                  </div>
                </template>
              </div>
            </el-card>
          </div>
  
          <!-- 右侧内容列 -->
          <div class="right-column">
            <!-- 物流状态卡片 -->
            <el-card class="shipping-status-card">
              <div class="card-header">
                <span>物流状态</span>
                <div class="header-actions">
                  <template v-if="!editingShippingStatus">
                    <el-button type="text" icon="el-icon-edit" @click="startEditShippingStatus">编辑</el-button>
                  </template>
                  <template v-else>
                    <el-button type="text" icon="el-icon-check" @click="saveShippingStatus">保存</el-button>
                    <el-button type="text" icon="el-icon-close" @click="cancelEditShippingStatus">取消</el-button>
                  </template>
                </div>
              </div>
              <div class="shipping-status">
                <template v-if="!editingShippingStatus">
                  <div class="status-timeline">
                    <div class="timeline-item" :class="{ active: orderData.shipping_status >= 0 }">
                      <div class="timeline-icon">
                        <i class="el-icon-shopping-cart-2"></i>
                      </div>
                      <div class="timeline-content">
                        <span class="status-text">待发货</span>
                      </div>
                    </div>
                    <div class="timeline-item" :class="{ active: orderData.shipping_status >= 1 }">
                      <div class="timeline-icon">
                        <i class="el-icon-truck"></i>
                      </div>
                      <div class="timeline-content">
                        <span class="status-text">已发货</span>
                      </div>
                    </div>
                    <div class="timeline-item" :class="{ active: orderData.shipping_status >= 2 }">
                      <div class="timeline-icon">
                        <i class="el-icon-check"></i>
                      </div>
                      <div class="timeline-content">
                        <span class="status-text">已送达</span>
                      </div>
                    </div>
                    <div class="timeline-item" :class="{ active: orderData.shipping_status >= 3 }">
                      <div class="timeline-icon">
                        <i class="el-icon-back"></i>
                      </div>
                      <div class="timeline-content">
                        <span class="status-text">已退货</span>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="status-edit-mode">
                    <div class="status-cards">
                      <div 
                        v-for="(status, index) in shippingStatusOptions" 
                        :key="index"
                        class="status-card"
                        :class="{ active: shippingStatusEdit.shipping_status === status.value }"
                        @click="shippingStatusEdit.shipping_status = status.value"
                      >
                        <div class="status-icon">
                          <i :class="status.icon"></i>
                        </div>
                        <span class="status-text">{{ status.label }}</span>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </el-card>
  
            <!-- 收货信息卡片 -->
            <el-card class="shipping-card">
              <div class="card-header">
                <span>收货信息</span>
                <div class="header-actions">
                  <template v-if="!editingShipping">
                    <el-button type="text" icon="el-icon-edit" @click="startEditShipping">编辑</el-button>
                  </template>
                  <template v-else>
                    <el-button type="text" icon="el-icon-check" @click="saveShipping">保存</el-button>
                    <el-button type="text" icon="el-icon-close" @click="cancelEditShipping">取消</el-button>
                  </template>
                </div>
              </div>
              <div class="shipping-info">
                <template v-if="!editingShipping">
                  <div class="shipping-grid">
                    <div class="shipping-item">
                      <span class="label">收货人</span>
                      <span class="value">{{ orderData.shipping_name || '-' }}</span>
                    </div>
                    <div class="shipping-item">
                      <span class="label">联系电话</span>
                      <span class="value">{{ orderData.shipping_phone || '-' }}</span>
                    </div>
                    <div class="shipping-item full-width" v-if="orderData.need_addr">
                      <span class="label">收货地址</span>
                      <span class="value">{{ orderData.shipping_address || '-' }}</span>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="shipping-grid">
                    <div class="shipping-item">
                      <span class="label">收货人</span>
                      <el-input v-model="shippingEdit.shipping_name"></el-input>
                    </div>
                    <div class="shipping-item">
                      <span class="label">联系电话</span>
                      <el-input v-model="shippingEdit.shipping_phone"></el-input>
                    </div>
                    <div class="shipping-item full-width">
                      <span class="label">收货地址</span>
                      <el-input type="textarea" v-model="shippingEdit.shipping_address" :rows="3"></el-input>
                    </div>
                  </div>
                </template>
              </div>
            </el-card>
            <!-- 退款信息卡片 -->
            <el-card class="refund-card" v-if="orderData.status === 3">
              <div class="card-header">
                <span>退款信息</span>
                <div class="header-actions">
                  <template v-if="!editingRefund">
                    <el-button type="text" icon="el-icon-edit" @click="startEditRefund">编辑</el-button>
                  </template>
                  <template v-else>
                    <el-button type="text" icon="el-icon-check" @click="saveRefund">保存</el-button>
                    <el-button type="text" icon="el-icon-close" @click="cancelEditRefund">取消</el-button>
                  </template>
                </div>
              </div>
              <div class="refund-info">
                <template v-if="!editingRefund">
                  <div class="refund-grid">
                    <div class="refund-item">
                      <span class="label">退款金额</span>
                      <span class="value price">¥{{ orderData.refund_amount }}</span>
                    </div>
                    <div class="refund-item">
                      <span class="label">退款时间</span>
                      <span class="value">{{ formatDateTime(orderData.refunded_at) || '-' }}</span>
                    </div>
                    <div class="refund-item full-width">
                      <span class="label">退款原因</span>
                      <span class="value">{{ orderData.refund_reason || '-' }}</span>
                    </div>
                    <div class="refund-item full-width" v-if="orderData.refund_evidence">
                      <span class="label">退款凭证</span>
                      <div class="evidence-image">
                        <img id="refund-img" :src="getImageUrl(orderData.refund_evidence, 'refund-img')" alt="加载失败">
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="refund-form">
                    <el-form :model="refundEdit" label-width="100px">
                      <el-form-item label="退款金额">
                        <el-input-number 
                          v-model="refundEdit.refund_amount"
                          :min="0"
                          :precision="2"
                          :step="0.01">
                        </el-input-number>
                      </el-form-item>
                      <el-form-item label="退款原因">
                        <el-input 
                          type="textarea"
                          v-model="refundEdit.refund_reason"
                          :rows="3">
                        </el-input>
                      </el-form-item>
                      <el-form-item label="退款凭证">
                        <div class="evidence-preview" v-if="refundEdit.refund_evidence">
                          <img id="refund-edit-img" :src="getImageUrl(refundEdit.refund_evidence, 'refund-edit-img')" alt="退款凭证">
                        </div>
                        <el-upload
                          class="evidence-uploader"
                          :action="getUploadUrl('payment_info')"
                          :headers="getHeaders()"
                          :show-file-list="false"
                          :before-upload="beforeUpload"
                          :on-success="(res) => handleUploadSuccess(res, 'refund')">
                          <el-button size="small" type="primary">
                            {{ refundEdit.refund_evidence ? '替换照片' : '点击上传' }}
                          </el-button>
                          <div slot="tip" class="el-upload__tip">只能上传jpg/png文件，且不超过10MB</div>
                        </el-upload>
                      </el-form-item>
                    </el-form>
                  </div>
                </template>
              </div>
            </el-card>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { getToken } from '@/utils/auth'
  import { BASE_URL } from '@/utils/urls'
  
  export default {
    name: 'ShopOrderDetails',
    data() {
      return {
        orderData: {},
        editingOrder: false,
        editingShippingStatus: false,
        editingShipping: false,
        editingRefund: false,
        orderEdit: {
          status: 0
        },
        shippingStatusEdit: {
          shipping_status: 0
        },
        shippingEdit: {
          shipping_name: '',
          shipping_phone: '',
          shipping_address: ''
        },
        refundEdit: {
          refund_amount: 0,
          refund_reason: '',
          refund_evidence: ''
        },
        shippingStatusOptions: [
          { value: 0, label: '待发货', icon: 'el-icon-shopping-cart-2' },
          { value: 1, label: '已发货', icon: 'el-icon-truck' },
          { value: 2, label: '已送达', icon: 'el-icon-check' },
          { value: 3, label: '已退货', icon: 'el-icon-back' }
        ],
        statusMap: {
          0: { type: 'danger', text: '待支付' },
          1: { type: 'primary', text: '已支付' },
          2: { type: 'info', text: '已取消' },
          3: { type: 'warning', text: '已退款' }
        },
        creatorInfo: {},
        updaterInfo: {}
      }
    },
    created() {
      this.getOrderDetails()
    },
    methods: {
      async getOrderDetails() {
        try {
          const res = await this.$http.get(`shop-orders/${this.$route.params.id}`)
          this.orderData = res.data
          
          // 获取创建者和更新者信息
          if (this.orderData.created_by) {
            await this.getUserInfo(this.orderData.created_by, 'creator')
          }
          if (this.orderData.updated_by) {
            await this.getUserInfo(this.orderData.updated_by, 'updater')
          }
        } catch (err) {
          this.$message.error('获取订单详情失败')
        }
      },
  
      async getUserInfo(userId, type) {
        try {
          const res = await this.$http.get(`users/${userId}`)
          switch (type) {
            case 'creator':
              this.creatorInfo = res.data
              break
            case 'updater':
              this.updaterInfo = res.data
              break
          }
        } catch (err) {
          console.error(`获取${type}信息失败:`, err)
        }
      },
  
      formatDateTime(date) {
        if (!date) return '';
        return new Date(date).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        })
      },
  
      getStatusType(status) {
        return this.statusMap[status]?.type || 'info'
      },
  
      getStatusText(status) {
        return this.statusMap[status]?.text || '未知'
      },
  
      getImageUrl(path, imageId) {
        this.$http({
          method: 'get',
          url: `images/confidential/${path}`,
          responseType: 'blob'
        }).then(res => {
          let objectURL = URL.createObjectURL(res);
          let img = document.getElementById(imageId);
          img.src = objectURL;
        }).catch(error => {
          console.error(error);
        });
      },
  
      goBack() {
        this.$router.push('/admin/shop/orders')
      },
  
      // 订单信息编辑
      startEditOrder() {
        this.editingOrder = true
        this.orderEdit = {
          status: this.orderData.status
        }
      },
  
      async saveOrder() {
        try {
          await this.$http.put(`shop-orders/${this.$route.params.id}`, this.orderEdit)
          this.editingOrder = false
          this.getOrderDetails()
        } catch (err) {
          this.$message.error(err.response?.data?.message || '订单信息更新失败')
        }
      },
  
      cancelEditOrder() {
        this.editingOrder = false
      },
  
      // 物流状态编辑
      startEditShippingStatus() {
        this.editingShippingStatus = true
        this.shippingStatusEdit = {
          shipping_status: this.orderData.shipping_status || 0
        }
      },
  
      async saveShippingStatus() {
        try {
          await this.$http.put(`shop-orders/${this.$route.params.id}`, this.shippingStatusEdit)
          this.editingShippingStatus = false
          this.getOrderDetails()
        } catch (err) {
          this.$message.error(err.response?.data?.message || '物流状态更新失败')
        }
      },
  
      cancelEditShippingStatus() {
        this.editingShippingStatus = false
      },
  
      // 收货信息编辑
      startEditShipping() {
        this.editingShipping = true
        this.shippingEdit = {
          shipping_name: this.orderData.shipping_name || '',
          shipping_phone: this.orderData.shipping_phone || '',
          shipping_address: this.orderData.shipping_address || '',
          shipping_status: this.orderData.shipping_status || 0
        }
      },
  
      async saveShipping() {
        try {
          await this.$http.put(`shop-orders/${this.$route.params.id}`, this.shippingEdit)
          this.editingShipping = false
          this.getOrderDetails()
        } catch (err) {
          this.$message.error(err.response?.data?.message || '收货信息更新失败')
        }
      },
  
      cancelEditShipping() {
        this.editingShipping = false
      },
  
      // 退款信息编辑
      startEditRefund() {
        this.editingRefund = true
        this.refundEdit = {
          refund_amount: this.orderData.refund_amount || 0,
          refund_reason: this.orderData.refund_reason || '',
          refund_evidence: this.orderData.refund_evidence || ''
        }
      },
  
      async saveRefund() {
        try {
          await this.$http.put(`shop-orders/${this.$route.params.id}`, this.refundEdit)
          this.editingRefund = false
          this.getOrderDetails()
        } catch (err) {
          this.$message.error(err.response?.data?.message || '退款信息更新失败')
        }
      },
  
      cancelEditRefund() {
        this.editingRefund = false
      },
  
      getUploadUrl(type) {
        return `${BASE_URL}/api/v1/images/confidential/${type}`
      },
  
      getHeaders() {
        return {
          'Authorization': `Bearer ${getToken()}`
        }
      },
  
      beforeUpload(file) {
        const isImage = file.type.startsWith('image/')
        const isLt10M = file.size / 1024 / 1024 < 10
  
        if (!isImage) {
          this.$message.error('只能上传图片文件!')
          return false
        }
        if (!isLt10M) {
          this.$message.error('图片大小不能超过 10MB!')
          return false
        }
        return true
      },
  
      handleUploadSuccess(response, type) {
        if (response.data) {
          if (type === 'refund') {
            this.refundEdit.refund_evidence = response.data
          }
          this.$message.success('上传成功')
        } else {
          this.$message.error('上传失败')
        }
      }
    }
  }
  </script>
  
  <style lang="less" scoped>
  .order-details {
    min-height: 100vh;
    overflow-x: hidden;
    box-sizing: border-box;
  
    .content-grid {
      display: grid;
      grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
      gap: 20px;
      margin: 0 auto;
  
      @media screen and (max-width: 1200px) {
        grid-template-columns: 1fr;
      }
    }
  
    .action-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px;
      background: #fff;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
      margin-bottom: 20px;
      border-radius: 8px;

      .date-info {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 48px;
        color: #888;
        font-size: 13px;
      }

      @media (max-width: 768px) {
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;

        .date-info {
          display: none;
        }

        .left-actions,
        .right-actions {
          flex-shrink: 0;
        }
      }
    }
  
    .el-card {
      margin-bottom: 20px;
      border-radius: 8px;
  
      .card-header {
        position: relative;
        padding: 8px 20px;
        border-radius: 8px;
        background: #fafafa;
        display: flex;
        align-items: center;
        justify-content: space-between;
  
        span {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
          padding-left: 10px;
          position: relative;
  
          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 2px;
            height: 12px;
            background: #1890ff;
            border-radius: 1px;
          }
        }
  
        .header-actions {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 1;
        }
      }
    }
  
    .product-title-card {
      margin-bottom: 20px;
  
      .product-title {
        display: flex;
        align-items: center;
        padding: 16px;
        background: linear-gradient(to right, #f8fafc, #fff);
        border-radius: 8px;
  
        .title-content {
          display: flex;
          align-items: center;
          flex: 1;
  
          i {
            font-size: 20px;
            color: #1890ff;
            margin-right: 12px;
          }
  
          span {
            font-size: 16px;
            font-weight: 500;
            color: #1f2937;
            line-height: 1.5;
          }
        }
      }
    }
  
    .order-info-card {
      .info-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        padding: 20px;
  
        .info-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
  
          &.full-width {
            grid-column: 1 / -1;
          }
  
          .info-item {
            background: #fafafa;
            padding: 12px 16px;
            border-radius: 8px;
            border: 1px solid #f0f0f0;
  
            .label {
              display: block;
              font-size: 13px;
              color: #666;
              margin-bottom: 8px;
            }
  
            .value {
              font-size: 14px;
              color: #262626;
  
              &.amount {
                color: #f56c6c;
                font-size: 20px;
                font-weight: 600;
                font-family: DIN Alternate, -apple-system, BlinkMacSystemFont, sans-serif;
                
                &::before {
                  content: '¥';
                  font-size: 16px;
                  margin-right: 2px;
                  font-weight: normal;
                }
              }
            }
  
            .full-width {
              width: 100%;
            }
          }
        }
      }
    }
  
    .shipping-status-card {
      margin-bottom: 20px;
  
      .shipping-status {
        padding: 24px;
  
        .status-timeline {
          position: relative;
          display: flex;
          justify-content: space-between;
          padding: 0 20px;
  
          &::before {
            content: '';
            position: absolute;
            top: 24px;
            left: 40px;
            right: 40px;
            height: 2px;
            background: #e5e7eb;
            z-index: 1;
          }
  
          .timeline-item {
            position: relative;
            z-index: 2;
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1;
  
            .timeline-icon {
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: #f3f4f6;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 12px;
              border: 2px solid #e5e7eb;
              transition: all 0.3s ease;
  
              i {
                font-size: 24px;
                color: #9ca3af;
              }
            }
  
            .timeline-content {
              text-align: center;
  
              .status-text {
                display: block;
                font-size: 14px;
                color: #6b7280;
              }
            }
  
            &.active {
              .timeline-icon {
                background: #1890ff;
                border-color: #1890ff;
  
                i {
                  color: #fff;
                }
              }
  
              .timeline-content {
                .status-text {
                  color: #1890ff;
                  font-weight: 500;
                }
              }
            }
          }
        }
  
        .status-edit-mode {
          padding: 20px;
  
          .status-cards {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
  
            .status-card {
              background: #f8fafc;
              border: 2px solid #e5e7eb;
              border-radius: 12px;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 12px;
              cursor: pointer;
              transition: all 0.3s ease;
  
              &:hover {
                background: #f1f5f9;
                border-color: #cbd5e1;
              }
  
              &.active {
                background: #e6f7ff;
                border-color: #1890ff;
  
                .status-icon {
                  background: #1890ff;
                  border-color: #1890ff;
  
                  i {
                    color: #fff;
                  }
                }
  
                .status-text {
                  color: #1890ff;
                  font-weight: 500;
                }
              }
  
              .status-icon {
                width: 48px;
                height: 48px;
                border-radius: 50%;
                background: #f3f4f6;
                display: flex;
                align-items: center;
                justify-content: center;
                border: 2px solid #e5e7eb;
                transition: all 0.3s ease;
  
                i {
                  font-size: 24px;
                  color: #9ca3af;
                }
              }
  
              .status-text {
                font-size: 14px;
                color: #6b7280;
                text-align: center;
              }
            }
          }
        }
      }
    }
  
    .shipping-card {
      .shipping-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        padding: 20px;
  
        .shipping-item {
          background: #fafafa;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #f0f0f0;
  
          &.full-width {
            grid-column: 1 / -1;
          }
  
          .label {
            display: block;
            font-size: 13px;
            color: #666;
            margin-bottom: 8px;
          }
  
          .value {
            font-size: 14px;
            color: #262626;
          }
  
          :deep(.el-input__inner),
          :deep(.el-textarea__inner) {
            background: #fff;
            border: 1px solid #dcdfe6;
            border-radius: 4px;
            padding: 8px 12px;
            font-size: 14px;
            color: #262626;
            transition: all 0.3s;
  
            &:hover {
              border-color: #c0c4cc;
            }
  
            &:focus {
              border-color: #409eff;
            }
          }
  
          :deep(.el-textarea__inner) {
            min-height: 80px;
            line-height: 1.5;
          }
        }
      }
    }
  
    .refund-card {
      .refund-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        padding: 20px;
  
        .refund-item {
          background: #fafafa;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #f0f0f0;
  
          &.full-width {
            grid-column: 1 / -1;
          }
  
          .label {
            display: block;
            font-size: 13px;
            color: #666;
            margin-bottom: 8px;
          }
  
          .value {
            font-size: 14px;
            color: #262626;
  
            &.price {
              color: #f56c6c;
              font-size: 16px;
              font-weight: 500;
            }
          }
  
          .evidence-image {
            margin-top: 8px;
  
            img {
              width: 100%;
              height: 120px;
              border-radius: 4px;
              object-fit: cover;
              overflow: hidden;
              display: inline-block;
              background-color: #F5F7FA;
              color: #C0C4CC;
              text-align: center;
              line-height: 120px;
              font-size: 14px;
            }
          }
        }
      }
  
      .refund-form {
        padding: 20px;
  
        .el-form-item {
          margin-bottom: 20px;
  
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    .evidence-preview {
      margin-bottom: 12px;
      
      img {
        width: 100%;
        height: 120px;
        object-fit: cover;
        border-radius: 4px;
        border: 1px solid #eee;
      }
    }

    .evidence-uploader {
      :deep(.el-upload__tip) {
        font-size: 12px;
        color: #666;
        margin-top: 8px;
      }
    }
  }
  
  .el-button {
    &--text {
      padding: 4px 8px;
      font-size: 13px;
  
      &:hover {
        background: rgba(24, 144, 255, 0.06);
        color: #1890ff;
      }
    }
  }
  
  .el-textarea :deep(.el-textarea__inner) {
    font-family: inherit;
    padding: 12px 16px;
    border-radius: 8px;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    .order-details {
      padding: 0 5px;
    }

    .content-grid {
      grid-template-columns: 1fr !important;
      gap: 10px;

      .left-column,
      .right-column {
        order: initial;
        transform: none;
      }
    }

    .el-card {
      margin-bottom: 10px !important;
      padding: 8px;
      overflow: hidden;
      min-width: 0;
    }

    .content-grid,
    .left-column,
    .right-column {
      min-width: 0;
    }

    .card-header {
      padding: 6px 10px !important;

      span {
        font-size: 12px !important;
        padding-left: 6px !important;
      }

      .header-actions {
        right: 8px;
      }
    }

    .product-title-card {
      margin-bottom: 10px !important;

      .product-title {
        padding: 0px !important;

        .title-content span {
          font-size: 14px !important;
        }

        .title-content i {
          font-size: 18px !important;
          margin-right: 8px !important;
        }
      }
    }

    .order-info-card {
      .info-grid {
        grid-template-columns: 1fr 1fr !important;
        gap: 10px !important;
        padding: 10px !important;

        .info-section {
          gap: 10px !important;
        }

        .info-item {
          padding: 10px !important;
          min-width: 0;

          .label {
            font-size: 12px !important;
            margin-bottom: 6px !important;
          }

          .value {
            font-size: 13px !important;

            &.amount {
              font-size: 16px !important;
            }
          }
        }
      }
    }

    .shipping-status-card {
      margin-bottom: 10px !important;

      .shipping-status {

        .status-timeline {
          flex-direction: row !important;
          align-items: flex-start !important;
          padding: 0 !important;

          &::before {
            display: none;
          }
        }
      }
    }

    .shipping-card {
      .shipping-grid {
        grid-template-columns: 1fr !important;
        gap: 10px !important;
        padding: 10px !important;

        .shipping-item {
          padding: 10px !important;
          min-width: 0;

          .label {
            font-size: 12px !important;
            margin-bottom: 6px !important;
          }

          .value {
            font-size: 13px !important;
          }

          :deep(.el-input__inner),
          :deep(.el-textarea__inner) {
            font-size: 12px !important;
            padding: 6px 10px !important;
            height: 32px;
          }
        }
      }
    }

    .refund-card {
      .refund-grid {
        grid-template-columns: 1fr !important;
        gap: 10px !important;
        padding: 10px !important;

        .refund-item {
          padding: 10px !important;
          min-width: 0;

          .label {
            font-size: 12px !important;
            margin-bottom: 6px !important;
          }

          .value {
            font-size: 13px !important;

            &.price {
              font-size: 14px !important;
            }
          }

          .evidence-image img {
            height: 100px !important;
          }
        }
      }

      .refund-form {
        padding: 10px !important;

        .el-form-item {
          margin-bottom: 15px !important;

          :deep(.el-input__inner),
          :deep(.el-textarea__inner) {
            font-size: 12px !important;
            padding: 6px 10px !important;
            height: 32px;
          }

          :deep(.el-input-number) {
            :deep(.el-input__inner) {
              font-size: 12px;
              height: 32px;
            }
          }
        }
      }
    }

    .evidence-preview img {
      height: 100px !important;
    }

    .el-tag {
      font-size: 10px !important;
      padding: 1px 5px !important;
    }

    .el-input__inner {
      font-size: 12px;
      height: 32px;
    }

    .el-textarea__inner {
      font-size: 12px;
    }

    .el-select .el-input__inner {
      font-size: 12px;
      height: 32px;
    }

    .el-dialog {
      width: 95% !important;
      margin: 0 auto;
      top: 5vh;
    }
  }
  </style>
  
  